import { abroadHreflang } from "@/lib/abroad/locale-head";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ABROAD_CITIES, ABROAD_CITY_BY_SLUG, ABROAD_SERVICES, abroadFaq } from "@/data/abroad";
import { absoluteUrl } from "@/config/sites";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { BIG_CLUSTER_REVIEWED_ISO, BIG_CLUSTER_REVIEWED_TH } from "@/lib/qa/big-cluster-labels";

export const Route = createFileRoute("/abroad/$city")({
  loader: ({ params }) => {
    const city = ABROAD_CITY_BY_SLUG[params.city];
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบหน้า" }, { name: "robots", content: "noindex" }] };
    }
    const c = loaderData.city;
    const url = absoluteUrl(`/abroad/${c.slug}`);
    const title = `อยู่${c.nameTh}ต้องใช้เอกสารไทย — รับรอง แปล เดินเรื่องแทน ส่งกลับ${c.countryTh}`;
    const desc = `คนไทยใน${c.nameTh} (${c.nameEn}) ใช้บริการรับรองเอกสารโดยทนาย Notary Public แปลรับรอง รับรองคำแปลกรมการกงสุล ${c.hague ? "Apostille" : "รับรองสถานทูต"} และขอเอกสารราชการแทนได้ทางไกล ส่งกลับราว ${c.courierDays} วันทำการ`;
    return {
      meta: [
        { title: ctrTitle(title) },
        { name: "description", content: ctrDesc(desc) },
        { property: "og:title", content: ctrTitle(title) },
        { property: "og:description", content: ctrDesc(desc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...abroadHreflang(`/${c.slug}`),
      ],
    };
  },
  component: AbroadCityPage,
  notFoundComponent: () => <div className="p-10 text-center text-sm">ไม่พบเมืองนี้</div>,
});

function AbroadCityPage() {
  const { city: c } = Route.useLoaderData();
  const url = absoluteUrl(`/abroad/${c.slug}`);
  const faq = abroadFaq(c);
  const nearby = ABROAD_CITIES.filter((x) => x.slug !== c.slug && x.countryCode === c.countryCode).slice(0, 6);
  const others = ABROAD_CITIES.filter((x) => x.countryCode !== c.countryCode).slice(0, 10);

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${url}#service`,
        name: `บริการเอกสารไทยสำหรับลูกค้าใน${c.nameTh}`,
        serviceType: "Notarisation, certified translation, consular legalisation",
        provider: { "@type": "LegalService", name: "Thai Notary Law", url: absoluteUrl("/") },
        areaServed: [
          { "@type": "City", name: c.nameEn },
          { "@type": "Country", name: c.countryEn, identifier: c.countryCode },
        ],
        availableChannel: { "@type": "ServiceChannel", serviceUrl: absoluteUrl("/contact") },
        dateModified: BIG_CLUSTER_REVIEWED_ISO,
      },
      {
        "@type": "FAQPage",
        url,
        dateModified: BIG_CLUSTER_REVIEWED_ISO,
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
        speakable: { "@type": "SpeakableSpecification", cssSelector: [".answer-summary"] },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
          { "@type": "ListItem", position: 2, name: "คนไทยในต่างประเทศ", item: absoluteUrl("/abroad") },
          { "@type": "ListItem", position: 3, name: c.nameTh, item: url },
        ],
      },
    ],
  };

  return (
    <>
      <JsonLd data={graph} />
      <Breadcrumbs
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "คนไทยในต่างประเทศ", to: "/abroad" },
          { label: c.nameTh },
        ]}
      />
      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-4 text-2xl font-bold">
          อยู่{c.nameTh} ({c.nameEn}) ต้องใช้เอกสารไทย — เราดำเนินการแทนจากกรุงเทพฯ
        </h1>
        <p className="answer-summary mb-6 leading-relaxed" data-speakable>
          ลูกค้าใน{c.nameTh} {c.countryTh} ไม่ต้องบินกลับไทย ส่งไฟล์สแกนมาให้ทีมทนายตรวจก่อนว่าหน่วยงานผู้รับต้องการเส้นทางรับรองแบบใด
          จากนั้นเราจัดทำคำแปล รับรองเอกสารโดยทนาย Notary Public เดินเรื่องรับรองคำแปลที่กรมการกงสุล
          และ{c.hague ? "ขอ Apostille ตามระบบอนุสัญญาเฮก" : "รับรองต่อที่สถานทูต/สำนักงานผู้แทนของประเทศปลายทาง เพราะยังไม่ใช้ Apostille"}
          แล้วจัดส่งกลับ ปกติขนส่งราว {c.courierDays} วันทำการต่อเที่ยว · เขตเวลา {c.timezone} · เว็บไซต์ไม่แสดงค่าบริการ
          ขอใบเสนอราคาเฉพาะเคสได้ทางโทรศัพท์ LINE หรืออีเมล · ตรวจทาน {BIG_CLUSTER_REVIEWED_TH}
        </p>

        <h2 className="mb-2 text-lg font-semibold">งานที่ลูกค้าใน{c.nameTh}ใช้บ่อย</h2>
        <p className="mb-6 text-sm leading-relaxed">{c.angleTh}</p>

        <h2 className="mb-2 text-lg font-semibold">บริการที่เลือกใช้ได้</h2>
        <ul className="mb-8 space-y-2 text-sm">
          {ABROAD_SERVICES.map((s) => (
            <li key={s.to}>
              <Link to={s.to as never} className="text-primary hover:underline">
                {s.label}
              </Link>{" "}
              — {s.note}
            </li>
          ))}
        </ul>

        <h2 className="mb-3 text-lg font-semibold">คำถามที่พบบ่อยจากลูกค้าใน{c.nameTh}</h2>
        <div className="mb-8 space-y-4">
          {faq.map((f) => (
            <div key={f.q}>
              <h3 className="text-sm font-semibold">{f.q}</h3>
              <p className="text-sm leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <p className="mb-6 text-xs text-muted-foreground">
          หมายเหตุ: สำนักงานของเราตั้งอยู่ในประเทศไทย ไม่มีสำนักงานใน{c.nameTh}
          ข้อมูลหน่วยงานที่อ้างถึง เช่น {c.mission} เป็นข้อมูลสาธารณะเพื่อประกอบการเตรียมเอกสารเท่านั้น
          และควรตรวจสอบข้อกำหนดล่าสุดกับหน่วยงานผู้รับก่อนยื่นทุกครั้ง
        </p>

        {nearby.length > 0 && (
          <>
            <h2 className="mb-2 text-lg font-semibold">เมืองอื่นใน{c.countryTh}</h2>
            <ul className="mb-6 grid gap-2 text-sm sm:grid-cols-2">
              {nearby.map((x) => (
                <li key={x.slug}>
                  <Link to="/abroad/$city" params={{ city: x.slug }} className="text-primary hover:underline">
                    {x.nameTh}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        <h2 className="mb-2 text-lg font-semibold">พื้นที่อื่นที่ให้บริการ</h2>
        <ul className="grid gap-2 text-sm sm:grid-cols-2">
          {others.map((x) => (
            <li key={x.slug}>
              <Link to="/abroad/$city" params={{ city: x.slug }} className="text-primary hover:underline">
                {x.nameTh} ({x.countryTh})
              </Link>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-sm">
          <Link to="/abroad" className="text-primary hover:underline">
            ดูทุกเมืองที่ให้บริการ →
          </Link>
        </p>
        <p className="mt-2 text-sm">
          <Link to="/contact" className="text-primary hover:underline">
            ส่งไฟล์สแกนให้ตรวจและขอใบเสนอราคาทางโทรศัพท์ / LINE / อีเมล →
          </Link>
        </p>
      </main>
    </>
  );
}
